import { ButtonGray, SearchField, UserSelectorDialog, Empty } from '@halo/ui-chat-react';
import type { SessionStoreInstance } from '@halo/chat-sdk-react';
import { StateEnum } from '@halo/chat-sdk-react';
import { Box, Button, Dialog, Stack } from '@chakra-ui/react';
import { tools } from '@halo/ui-chat-react';
import { useStore } from 'zustand';
import { useUserInfoList } from '@/scripts/hooks/useUserInfoList';
import type { GroupJoinRequest } from './JoinItem';
import { useSearch } from './helpers';
import { JoinItem } from './JoinItem';

interface JoinRequestsProps {
  isOpen: boolean;
  checkUserKey?: boolean;
  setIsOpen: (isOpen: boolean) => void;
  sessionStoreInstance: SessionStoreInstance;
  onMemberAvatarClick?: (member: GroupJoinRequest) => void;
}
const GroupJoinRequestsDialog = ({
  isOpen,
  setIsOpen,
  sessionStoreInstance,
  onMemberAvatarClick,
  checkUserKey = true,
}: JoinRequestsProps) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [resetTrigger, setResetTrigger] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const sessionStore = useStore(sessionStoreInstance);
  const groupJoinUserIds = useMemo(() => sessionStore?.groupJoins, [sessionStore?.groupJoins]);

  const { users } = useUserInfoList<GroupJoinRequest>(groupJoinUserIds);
  const groupJoinUsers = useMemo(() => {
    return (
      users?.map((user) => ({
        ...user,
        added: false,
      })) ?? []
    );
  }, [users]);

  const { list } = useSearch(searchValue, resetTrigger, isOpen, groupJoinUsers);

  const listFormatted = useMemo(() => {
    return list.map(({ items }) => {
      const filteredItems = items?.map((item) => {
        //todo
        return item;
      });
      return { items: filteredItems };
    });
  }, [list]);

  const {
    listFlat,
    selected,
    isSelectedAll,
    toggleSelectUser,
    onSelectAllChange,
    resetDialogState,
  } = UserSelectorDialog.useUserSelector(listFormatted, checkUserKey);

  const onAccept = useCallback(
    async (members: GroupJoinRequest[]) => {
      try {
        const result = await sessionStore?.client?.Api.session.groupJoinsAccept(
          sessionStore?.session?.chat_id ?? '',
          members.map((item) => item.user_id)
        );
        if (result?.not_allow_list?.length) {
          tools.toaster.error({
            title: t('chatSettings.userFailedToAccept', {
              count: result?.not_allow_list?.length,
            }),
          });
        }
      } catch (error) {
        console.error('onAccept', error);
      }
    },
    [sessionStore?.client?.Api.session, sessionStore?.session?.chat_id, t]
  );

  const onReject = useCallback(
    async (members: GroupJoinRequest[]) => {
      try {
        const result = await sessionStore?.client?.Api.session.groupJoinsReject(
          sessionStore?.session?.chat_id ?? '',
          members.map((item) => item.user_id)
        );
        if (result?.not_allow_list?.length) {
          tools.toaster.error({
            title: t('chatSettings.userFailedToReject', {
              count: result?.not_allow_list?.length,
            }),
          });
        }
        if (result?.existing_list?.length) {
          tools.toaster.error({
            title: t('chatSettings.userAlreadyInGroup', {
              count: result?.existing_list?.length,
            }),
          });
        }
      } catch (error) {
        console.error('onReject', error);
      }
    },
    [sessionStore?.client?.Api.session, sessionStore?.session?.chat_id, t]
  );

  useLayoutEffect(() => {
    if (!isOpen) {
      resetDialogState();
      setSearchValue('');
      setResetTrigger((prev) => prev + 1);
    }
  }, [isOpen, resetDialogState]);

  // useEffect(() => {
  //   console.log('============groupJoinUserIds', groupJoinUserIds);
  //   if (groupJoinUserIds?.length === 0) {
  //     setIsOpen(false);
  //   }
  // }, [groupJoinUserIds, setIsOpen]);

  return (
    <Dialog.Root placement="center" open={isOpen} onOpenChange={(e) => setIsOpen?.(e.open)}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content rounded="xl" w="604px" maxW="604px" bg="bg">
          <Dialog.Header pt="5" px="5" pb="0" justifyContent="space-between">
            <Dialog.Title fontWeight="semibold" fontSize="md">
              {t('chatSettings.joinRequests')}
            </Dialog.Title>
          </Dialog.Header>
          <Dialog.Body px="3.5" pt="2" pb="1">
            <Box m="2" border="1px solid" borderColor="border.subtle" rounded="xl" pt="3" pb="0">
              <Stack gap="2" px="3">
                <SearchField searchValue={searchValue} onChange={setSearchValue} />
                {/* <Text
                  py="1"
                  pl="2"
                  fontSize="sm"
                  fontWeight="medium"
                  color="fg.subtle"
                  lineHeight="normal"
                >
                  Pending
                </Text> */}
                <Box display="flex" flexDirection="column" minH="360px" maxH="360px">
                  <Stack gap="0" flex="1" overflowY="auto">
                    {listFlat?.length === 0 && <Empty>{t('chatSettings.noJoinRequests')}</Empty>}
                    {listFlat?.map((member, index) => {
                      const isKeyErr = checkUserKey
                        ? !(member.have_group_key && member.have_user_key)
                        : false;
                      const isChecked = selected.some((i) => i.user_id === member.user_id);
                      const isDisabled = isKeyErr || member.state === StateEnum.DELETE_ACCOUNT;

                      const onClick = () => {
                        if (isDisabled) return;
                        toggleSelectUser(member);
                      };
                      return (
                        <JoinItem
                          key={member.user_id}
                          member={member}
                          onMemberAvatarClick={onMemberAvatarClick}
                          isChecked={isChecked}
                          isDisabled={isDisabled}
                          onClick={onClick}
                          isLast={index === (listFlat?.length ?? 0) - 1}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              </Stack>
              <Box
                p="3"
                pt="0"
                borderTop="1px solid"
                borderColor="border.subtle"
                fontSize="xs"
                boxShadow="0px -1px 8px 0px rgba(0, 0, 0, 0.06)"
              >
                <UserSelectorDialog.SelectAll
                  isChecked={isSelectedAll}
                  onChange={onSelectAllChange}
                />
              </Box>
            </Box>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.ActionTrigger asChild>
              <ButtonGray
                minW="96px"
                borderRadius="xl"
                disabled={selected.length === 0 || isLoading}
                onClick={async () => {
                  setIsLoading(true);
                  await onReject?.(selected).finally(() => {
                    setIsOpen(false);
                    setIsLoading(false);
                  });
                }}
              >
                {t('buttons.reject')}
              </ButtonGray>
            </Dialog.ActionTrigger>
            <Button
              minW="96px"
              disabled={selected.length === 0 || isLoading}
              borderRadius="xl"
              onClick={async () => {
                setIsLoading(true);
                await onAccept?.(selected).finally(() => {
                  setIsOpen(false);
                  setIsLoading(false);
                });
              }}
            >
              {t('buttons.accept')} ({selected.length})
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};

const MemoizedJoinRequest = memo(GroupJoinRequestsDialog);
export { MemoizedJoinRequest as GroupJoinRequestsDialog };
